import { useState, useEffect, useCallback } from 'react';

type EmbedTheme = 'dark' | 'light';

/**
 * Hook to detect and manage embed/iframe mode
 * 
 * Embed mode is triggered by:
 * 1. Being inside an iframe
 * 2. ?embed=true query parameter (useful for testing)
 * 
 * Theme can be set via:
 * - ?theme=light or ?theme=dark (default is dark)
 * 
 * In embed mode:
 * - Header/branding is hidden
 * - Tighter spacing is used
 * - Height is communicated to parent for seamless resizing
 */
export function useEmbedMode() {
    const [isEmbedded, setIsEmbedded] = useState(false);
    const [theme, setTheme] = useState<EmbedTheme>('dark');

    useEffect(() => {
        // Check if in iframe
        const inIframe = window.self !== window.top;
        
        // Check for query params
        const urlParams = new URLSearchParams(window.location.search);
        const embedParam = urlParams.get('embed') === 'true';
        const themeParam = urlParams.get('theme') as EmbedTheme | null;

        setIsEmbedded(inIframe || embedParam);
        
        // Set theme (default to dark, allow light)
        if (themeParam === 'light') {
            setTheme('light');
        }

        // Add classes to body for CSS targeting
        if (inIframe || embedParam) {
            document.body.classList.add('embed-mode');
        } else {
            document.body.classList.remove('embed-mode');
        }

        // Add theme class
        if (themeParam === 'light') {
            document.body.classList.add('theme-light');
            document.body.classList.remove('theme-dark');
        } else {
            document.body.classList.add('theme-dark');
            document.body.classList.remove('theme-light');
        }

        return () => {
            document.body.classList.remove('embed-mode');
            document.body.classList.remove('theme-light');
            document.body.classList.remove('theme-dark');
        };
    }, []);

    // Function to notify parent of height changes
    const notifyParentHeight = useCallback(() => {
        if (!isEmbedded) return;

        try {
            const height = document.documentElement.scrollHeight;
            window.parent.postMessage({ type: 'letsmeet-resize', height }, '*');
        } catch {
            // Cross-origin restrictions may prevent this
        }
    }, [isEmbedded]);

    // Set up resize observer to notify parent of height changes
    useEffect(() => {
        if (!isEmbedded) return;

        // Initial height notification
        notifyParentHeight();

        // Watch for DOM changes that might affect height
        const observer = new ResizeObserver(() => {
            notifyParentHeight();
        });

        observer.observe(document.body);

        // Also notify on window resize
        window.addEventListener('resize', notifyParentHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', notifyParentHeight);
        };
    }, [isEmbedded, notifyParentHeight]);

    return { isEmbedded, theme, notifyParentHeight };
}
